import { Box, Flex, Stack, Text } from "flicket-ui";

import {
  IReleaseTicket,
  IReleaseZone,
} from "~features/nonseated-reservation/nonseated-reservation.types";
import { useShowHeaderBanner } from "~components/common/common.HeaderAlertBanner";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import StickySection from "~components/common/StickySection";
import { addQueryParams } from "~lib/helpers/queryParams";
import { SCROLL_IN_PROGRESS } from "./useIntersection.hook";
import styled from "styled-components";
import { useAccount } from "~hooks";

export const TABS_HEIGHT = 60;

export function StickyZoneTabs({
  zones,
  addons,
}: {
  zones: IReleaseZone[];
  addons: IReleaseTicket[];
}) {
  const router = useRouter();
  const { scrollToZone } = useScrollToZone();
  const { activeBannerHeight } = useShowHeaderBanner();
  const { authState } = useAccount();

  function handleClick(id: string) {
    scrollToZone(id);
    addQueryParams(router, { zoneId: id });
  }

  // Handle initial scroll to zone on load if zoneId is in the query. A little complex:
  // 1. we need to wait for the auth state to be known before scrolling so
  //   we can take into account any header banner heights and
  // 2. we want to set the scroll in progress class to prevent the intersection observer
  //   removing the zoneId prop from the header on page load.
  useEffect(() => {
    if (router.query.zoneId) {
      document.body.classList.add(
        `${SCROLL_IN_PROGRESS}-${router.query.zoneId as string}`
      );
      if (authState !== "authenticating") {
        scrollToZone(router.query.zoneId as string);
      }
    }
  }, [authState]);

  // If the zone Id has changed, make sure the tab is selected and in view
  useEffect(() => {
    if (router.query.zoneId) {
      const viewWidth = window.innerWidth;
      const tabsEl = document.getElementById("sticky-zone-tabs");
      const tabScrollerEl = tabsEl.querySelector(".scroller");
      const tabsWidth = tabsEl?.offsetWidth ?? 0;
      const scrollWidth = tabScrollerEl?.scrollWidth ?? 0;

      const isOverflowing = scrollWidth > tabsWidth;

      if (isOverflowing) {
        const activeTabEl: HTMLDivElement = tabsEl.querySelector(
          `[data-active="true"]`
        );

        // Awesome maths to center the active tab in the viewport if possible
        const offsetLeft = activeTabEl?.offsetLeft;
        const viewportOffsetLeft = viewWidth / 2 - activeTabEl?.offsetWidth / 2;
        const left = offsetLeft - viewportOffsetLeft;

        tabScrollerEl.scrollTo({
          behavior: "smooth",
          left,
        });
      }
    }
  }, [router?.query?.zoneId]);

  return (
    <StickySection offset={activeBannerHeight}>
      {({ isSticky }) => (
        <Box
          id="sticky-zone-tabs"
          boxShadow={isSticky ? "card" : undefined}
          backgroundColor="white"
          mb={{ _: 3, sm: 6 }}
          width="100vw"
          height={`${TABS_HEIGHT}px`}
        >
          <Box maxW={{ sm: 960 }} margin={"auto" as any}>
            <Box
              borderBottom="1px solid"
              borderBottomColor={isSticky ? "transparent" : "N200"}
            >
              <Stack
                className="scroller"
                direction="horizontal"
                gap={2}
                w="100%"
                overflowX="auto"
                pr={2}
                pl={2}
                position={"relative"}
                // Adjust for the bottom border overlap
                top={"1px" as any}
                zIndex={1}
                css={`
                  ::-webkit-scrollbar {
                    display: none;
                  }
                `}
              >
                {zones
                  .filter((z) => {
                    // TODO: incorporate "isAvailableForSelection" into the filter
                    // Hoist to top level data transforms?
                    return (
                      z.isActive && z.ticketTypes.some((tt) => tt.isActive)
                    );
                  })
                  .map((zone, i) => {
                    const isSelected =
                      (!router.query.zoneId && i === 0) ||
                      router.query.zoneId === zone.id;
                    return (
                      <TabItem
                        key={zone.id}
                        name={zone.name}
                        isSelected={isSelected}
                        onClick={() => {
                          handleClick(zone.id);
                        }}
                      />
                    );
                  })}

                {addons?.length > 0 && (
                  <TabItem
                    key={"addons"}
                    name="Add-ons"
                    isSelected={router.query.zoneId === "add-ons"}
                    onClick={() => handleClick("add-ons")}
                  />
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      )}
    </StickySection>
  );
}

const StyledText = styled(Text)`
  text-wrap: nowrap;
  -webkit-tap-highlight-color: transparent;

  @media (hover: hover) {
    /* Target only mouse pointer devices so mobile doesnt get sticky hover effects
     https://stackoverflow.com/questions/17233804/how-to-prevent-sticky-hover-effects-for-buttons-on-touch-devices/28058919#28058919 
    */
    &:hover {
      color: ${(p) => p.theme.colors.N600};
    }
  }
`;

function TabItem({
  onClick,
  name,
  isSelected,
}: {
  onClick: () => void;
  name: string;
  isSelected: boolean;
}) {
  return (
    <Flex
      onClick={onClick}
      alignItems="flex-end"
      flexWrap="nowrap"
      data-active={isSelected}
    >
      <StyledText
        variant="header.XS"
        cursor="pointer"
        py={2}
        borderBottom="3px solid"
        borderColor={isSelected ? "N800" : "transparent"}
      >
        {name}
      </StyledText>
    </Flex>
  );
}

function useScrollToZone() {
  const { activeBannerHeight } = useShowHeaderBanner();
  const [isScrolling, setScrolling] = useState(false);

  function scrollToZone(id: string, onComplete?: () => void) {
    setScrolling(true);
    const className = `${SCROLL_IN_PROGRESS}-${id}`;

    if (document.body.classList.contains(className)) return;

    document.body.classList.add(className);

    const zoneEl = document.getElementById(id);

    if (!zoneEl) return;

    const offset = activeBannerHeight + (TABS_HEIGHT - 2);

    const top =
      window.scrollY +
      document.getElementById(id)?.getBoundingClientRect().top -
      offset;

    window.scrollTo({
      behavior: "smooth",
      top,
    });

    function handleScrollEnd() {
      document.body.classList.remove(className);

      setScrolling(false);

      onComplete?.();
    }

    // Since "scrollend" event has limited support use an arbitrary timeout
    // to simulate page scroll finish, rather than relying on scroll events.
    setTimeout(handleScrollEnd, 1500);
  }

  return {
    isScrolling,
    scrollToZone,
  };
}
